import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Check, ArrowRight, TrendingUp, LineChart, Globe2, Briefcase, Layers, Building2, Banknote, Wallet, ScrollText, Repeat, Hourglass, Network } from "lucide-react";
import { findService, SERVICES } from "../lib/services";
import { ConsultationDialog } from "../components/ConsultationDialog";

const ICONS = { TrendingUp, LineChart, Globe2, Briefcase, Layers, Building2, Banknote, Wallet, ScrollText, Repeat, Hourglass, Network };

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = findService(slug);
  if (!service) return <Navigate to="/services" replace />;
  const Icon = ICONS[service.icon] || Briefcase;

  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <main data-testid="service-detail-page" className="pt-32">
      <section className="pb-12">
        <div className="container-myc">
          <Link
            to="/services"
            data-testid="back-to-services-link"
            className="inline-flex items-center gap-2 text-slate-600/60 hover:text-[#7A1F26] text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all services
          </Link>
        </div>
      </section>

      <section className="pb-16">
        <div className="container-myc grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="w-14 h-14 border border-[#7A1F26]/40 inline-flex items-center justify-center mb-7">
              <Icon className="w-6 h-6 text-[#7A1F26]" />
            </div>
            <p className="eyebrow">Service</p>
            <h1 className="h-display-1 text-[#2A0A0E] mt-5">{service.title}</h1>
            <p className="text-slate-600/70 mt-7 text-lg leading-relaxed max-w-2xl">
              {service.short}
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#FAF5EB]">
        <div className="container-myc grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <p className="eyebrow">Approach</p>
            <h2 className="h-display-3 text-[#2A0A0E] mt-4">
              How we deliver this service
            </h2>
            <p className="text-slate-600/70 mt-6 leading-relaxed">
              {service.description}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="card-myc">
              <p className="eyebrow">What's included</p>
              <ul className="mt-5 space-y-4">
                {service.points.map((p) => (
                  <li key={p} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#7A1F26] mt-0.5 shrink-0" />
                    <span className="text-slate-600/80 text-[15px]">{p}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                <ConsultationDialog
                  defaultService={service.title}
                  trigger={
                    <button
                      data-testid="service-detail-consult-button"
                      className="btn-gold w-full"
                    >
                      Discuss this service <ArrowRight className="w-4 h-4" />
                    </button>
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-myc">
          <p className="eyebrow">Other services</p>
          <h2 className="h-display-3 text-[#2A0A0E] mt-3 mb-10">
            Continue exploring
          </h2>
          <div className="grid md:grid-cols-3 gap-px bg-slate-900/5 border border-slate-200/5">
            {others.map((s) => {
              const I = ICONS[s.icon] || Briefcase;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="bg-[#FAF5EB] hover:bg-[#FAF5EB] p-7 transition-colors group"
                >
                  <I className="w-5 h-5 text-[#7A1F26]" />
                  <h3 className="font-display text-lg text-[#2A0A0E] mt-5 group-hover:text-[#7A1F26] transition-colors">
                    {s.title}
                  </h3>
                  <p className="text-sm text-slate-600/55 mt-2">{s.short}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
}
